import React from "react";
import { ChevronRight } from "lucide-react";
import { Card, SectionTitle, InfoIcon, DeltaChip, RowPop } from "./ui";
import {
  DIM_BY_KEY,
  availableDims,
  breakdownRows,
  type BreakdownRow,
  type DimKey,
  type DrillSegment,
  type HStudent,
} from "../data/hierarchy";

const fmt = (n: number) => n.toLocaleString("en-US");

// ---- Interactive breadcrumb (replaces the passive "Showing:" line) ---------
export function MetricBreadcrumb({
  metricLabel,
  path,
  onOverview,
  onMetric,
  onNavigate,
}: {
  metricLabel?: string;
  path: DrillSegment[];
  onOverview: () => void;
  onMetric: () => void;
  onNavigate: (index: number) => void;
}) {
  const crumbs = [
    { label: "Overview", action: onOverview },
    ...(metricLabel ? [{ label: metricLabel, action: onMetric }] : []),
    ...path.map((seg, i) => ({
      label: seg.label,
      action: () => onNavigate(i),
    })),
  ];
  return (
    <div className="flex flex-wrap items-center gap-1 text-[13px]">
      {crumbs.map((c, i) => {
        const isCurrent = i === crumbs.length - 1;
        return (
          <React.Fragment key={`${c.label}-${i}`}>
            {i > 0 && (
              <ChevronRight
                size={14}
                strokeWidth={2.2}
                className="shrink-0 text-[#687368] opacity-60"
              />
            )}
            <button
              onClick={c.action}
              disabled={isCurrent}
              className={`rounded-md border-none bg-transparent px-1 py-0.5 text-[13px] ${
                isCurrent
                  ? "cursor-default font-bold text-[#294B39]"
                  : "cursor-pointer font-semibold text-[#28543D]"
              }`}
            >
              {c.label}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ---- "Break down by" dimension picker (inline segmented pills) --------------
function BreakdownByControl({
  dim,
  path,
  onChange,
}: {
  dim: DimKey;
  path: DrillSegment[];
  onChange: (d: DimKey) => void;
}) {
  const options = availableDims(path).filter(
    (option) => option.key !== "coteacher" && option.key !== "student",
  );
  return (
    <div className="flex flex-wrap items-center justify-end gap-2.5">
      <div className="inline-flex flex-wrap rounded-[6px] border border-[#DED8CC] bg-[#F3EDE2] p-[3px]">
        {options.map((option) => {
          const active = option.key === dim;
          return (
            <button
              key={option.key}
              onClick={() => onChange(option.key)}
              className={`cursor-pointer rounded-[4px] border-none px-3 py-[5px] text-[12.5px] font-bold ${
                active
                  ? "bg-[#FBF8F1] text-[#28543D] shadow-[0_1px_3px_rgba(0,0,0,0.1)]"
                  : "bg-transparent text-[#687368]"
              }`}
            >
              {option.noun}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---- A single breakdown row (drillable or leaf) ----------------------------
const COLS = "1.8fr 1.3fr 1fr 1.2fr";

function BreakdownRowView({ row }: { row: BreakdownRow }) {
  const m = row.metrics;
  const activePct =
    m.total > 0 ? Math.round((m.activeCount / m.total) * 100) : 0;
  return (
    <div
      className="group/row relative m-0 grid cursor-default items-center gap-4 border-t border-[#DED8CC] px-2 py-3.5"
      style={{ gridTemplateColumns: COLS, marginInline: "-8px" }}
    >
      {/* Entity */}
      <div className="flex min-w-0 items-center gap-2">
        <span className="w-4 shrink-0" />
        <div className="flex min-w-0 flex-col gap-1">
          <span className="truncate text-sm font-bold text-[#294B39]">
            {row.label}
          </span>
          <span className="truncate text-[12.5px] text-[#687368]">
            {row.sublabel}
          </span>
        </div>
      </div>

      {/* Active students */}
      <div className="flex flex-col items-end gap-1.5">
        <span className="text-sm text-[#687368]">
          <strong className="font-bold text-[#294B39]">
            {fmt(m.activeCount)}
          </strong>{" "}
          of {fmt(m.total)}
        </span>
        <div className="h-1.5 w-[120px] overflow-hidden rounded-full bg-[#28543D]/5">
          <div
            className="h-full rounded-full bg-[#6E9A7B]"
            style={{ width: `${activePct}%` }}
          />
        </div>
      </div>

      {/* Avg hrs per student */}
      <span className="text-right text-[15px] text-[#687368]">
        <strong className="font-bold text-[#294B39]">
          {m.avgHours.toFixed(1)}
        </strong>{" "}
        hrs
      </span>

      {/* % on track */}
      <div className="flex items-center justify-end gap-2.5">
        <span className="text-[15px] font-bold text-[#294B39]">
          {m.onTrackPct}%
        </span>
        <DeltaChip delta={row.delta} suffix="%" compareLabel="" />
      </div>

      <RowPop
        title={`${row.label} breakdown`}
        items={[
          { label: "Students", value: fmt(m.total) },
          {
            label: "Active (30d)",
            value: `${fmt(m.activeCount)} (${activePct}%)`,
          },
          { label: "Avg hrs / student", value: `${m.avgHours.toFixed(1)} hrs` },
          {
            label: "On track",
            value: `${m.onTrackPct}% (${row.delta >= 0 ? "+" : ""}${row.delta})`,
          },
          { label: "Avg time / day", value: `${m.avgMin} min` },
        ]}
      />
    </div>
  );
}

// ---- The drillable breakdown section ---------------------------------------
export function BreakdownExplorer({
  scoped,
  path,
  dim,
  onDim,
}: {
  scoped: HStudent[];
  path: DrillSegment[];
  dim: DimKey;
  onDim: (d: DimKey) => void;
}) {
  const meta = DIM_BY_KEY[dim];
  const rows = breakdownRows(scoped, dim);
  const truncated = dim === "student" && scoped.length > rows.length;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <SectionTitle title={meta.by} />
        <BreakdownByControl dim={dim} path={path} onChange={onDim} />
      </div>
      <Card className="flex flex-col">
        <div
          className="grid gap-4 pb-2.5 text-xs font-bold text-[#687368]"
          style={{ gridTemplateColumns: COLS }}
        >
          <span className="pl-6">{meta.noun}</span>
          <span className="inline-flex items-center justify-end gap-1">
            Active students
            <InfoIcon
              text="Students who spent at least 4 hours learning in the last 30 days, out of the students in this group."
              align="right"
            />
          </span>
          <span className="inline-flex items-center justify-end gap-1">
            Avg hrs per student
            <InfoIcon
              text="Average 1:1 AI-tutor time per student in this group for the selected period."
              align="right"
            />
          </span>
          <span className="inline-flex items-center justify-end gap-1">
            % on track
            <InfoIcon
              text="Share of this group's active students on track to reach grade-level proficiency by year end, with change vs last month."
              align="right"
            />
          </span>
        </div>
        {rows.length === 0 ? (
          <div className="py-4 text-[13.5px] text-[#687368]">
            No students match the current scope.
          </div>
        ) : (
          rows.map((r) => (
            <BreakdownRowView key={`${r.dim}-${r.value}`} row={r} />
          ))
        )}
        {truncated && (
          <div className="mt-0.5 border-t border-[#DED8CC] pt-3 text-[12.5px] text-[#687368]">
            Showing top {rows.length} of {scoped.length.toLocaleString("en-US")}{" "}
            students by tutoring hours.
          </div>
        )}
      </Card>
    </section>
  );
}
